<template>
  <Teleport to="body">
    <Transition name="contract-preview-fade">
      <div
        v-if="open"
        class="contract-preview-mask"
        :class="{ mobile: variant === 'mobile' }"
        role="presentation"
        @click.self="requestClose"
      >
        <section
          class="contract-preview-dialog"
          role="dialog"
          aria-modal="true"
          :aria-label="preview?.title || '合同在线预览'"
        >
          <header class="contract-preview-header">
            <div>
              <strong>{{ preview?.title || '合同在线预览' }}</strong>
              <small v-if="preview?.contractNo">合同编号：{{ preview.contractNo }}</small>
            </div>
            <button type="button" aria-label="关闭预览" @click="requestClose">×</button>
          </header>

          <div class="contract-preview-body">
            <div v-if="loading" class="contract-preview-state">
              <span class="contract-preview-spinner" aria-hidden="true"></span>
              <strong>{{ loadingTitle }}</strong>
              <span>系统正在读取合同并转换为网页内容。</span>
            </div>
            <div v-else-if="error" class="contract-preview-state error">
              <strong>合同预览打开失败</strong>
              <span>{{ error }}</span>
              <button type="button" @click="$emit('retry')">重新加载</button>
            </div>
            <DocumentViewer
              v-else-if="preview?.contentHtml"
              class="contract-preview-viewer"
              :document="viewerDocument"
              :title="preview.title"
            />
            <div v-else class="contract-preview-state">
              <strong>合同内容为空</strong>
              <span>请稍后刷新重试，或直接下载合同文件。</span>
            </div>
          </div>

          <footer class="contract-preview-footer">
            <p>{{ footerHint }}</p>
            <button
              type="button"
              class="download-button"
              :disabled="downloading || !preview?.fileName"
              @click="$emit('download')"
            >
              {{ downloading ? '正在下载...' : downloadText }}
            </button>
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import DocumentViewer from '@/components/DocumentViewer.vue'

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  preview: {
    type: Object,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: ''
  },
  downloading: {
    type: Boolean,
    default: false
  },
  variant: {
    type: String,
    default: 'pc',
    validator: value => ['pc', 'mobile'].includes(value)
  },
  templateMode: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'download', 'retry'])

const viewerDocument = computed(() => ({
  documentType: 'doc',
  originalFileName: props.preview?.fileName || 'contract.docx',
  contentHtml: props.preview?.contentHtml || ''
}))

const loadingTitle = computed(() => (props.templateMode ? '正在读取合同模板' : '正在生成合同预览'))

const footerHint = computed(() => (props.templateMode ? '模板预览仅用于阅读确认；支付后生成的正式合同以系统文件为准。'
  : '在线预览用于快速阅读；正式签署请以下载的 DOCX 合同为准。'))

const downloadText = computed(() => (props.templateMode ? '下载合同模板' : '下载合同'))

/** 请求关闭弹窗；加载中不允许误触遮罩关闭。 */
function requestClose() {
  if (!props.loading) {
    emit('close')
  }
}
</script>

<style scoped>
.contract-preview-mask {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  background: rgba(5, 12, 24, 0.72);
  backdrop-filter: blur(6px);
}

.contract-preview-dialog {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  width: min(1080px, 100%);
  height: min(86vh, 860px);
  overflow: hidden;
  border: 1px solid rgba(147, 197, 253, 0.22);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 28px 80px rgba(2, 6, 23, 0.42);
}

.contract-preview-header,
.contract-preview-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 18px;
}

.contract-preview-header {
  border-bottom: 1px solid #e2e8f0;
}

.contract-preview-header strong {
  display: block;
  color: #0f172a;
  font-size: 16px;
  line-height: 1.4;
}

.contract-preview-header small {
  display: block;
  margin-top: 3px;
  color: #64748b;
  font-size: 12px;
}

.contract-preview-header button {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  color: #64748b;
  background: #f1f5f9;
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
}

.contract-preview-header button:hover {
  color: #0f172a;
  background: #e2e8f0;
}

.contract-preview-body {
  min-height: 0;
  overflow: hidden;
  background: #f8fafc;
}

.contract-preview-viewer {
  height: 100%;
}

.contract-preview-state {
  display: grid;
  min-height: 100%;
  align-content: center;
  justify-items: center;
  gap: 10px;
  padding: 32px;
  color: #64748b;
  text-align: center;
}

.contract-preview-state strong {
  color: #0f172a;
  font-size: 17px;
}

.contract-preview-state.error strong {
  color: #b91c1c;
}

.contract-preview-state button {
  min-height: 38px;
  margin-top: 6px;
  padding: 0 16px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: #0f766e;
  cursor: pointer;
}

.contract-preview-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #ccfbf1;
  border-top-color: #0f766e;
  border-radius: 50%;
  animation: contract-preview-spin 0.8s linear infinite;
}

.contract-preview-footer {
  border-top: 1px solid #e2e8f0;
  background: #fff;
}

.contract-preview-footer p {
  margin: 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.6;
}

.download-button {
  min-height: 40px;
  flex: 0 0 auto;
  padding: 0 18px;
  border: 0;
  border-radius: 9px;
  color: #fff;
  background: #0f766e;
  cursor: pointer;
  font-weight: 700;
}

.download-button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.contract-preview-fade-enter-active,
.contract-preview-fade-leave-active {
  transition: opacity 0.18s ease;
}

.contract-preview-fade-enter-from,
.contract-preview-fade-leave-to {
  opacity: 0;
}

.contract-preview-mask.mobile {
  align-items: stretch;
  padding: 0;
}

.contract-preview-mask.mobile .contract-preview-dialog {
  width: 100%;
  height: 100%;
  border-radius: 0;
}

.contract-preview-mask.mobile .contract-preview-header,
.contract-preview-mask.mobile .contract-preview-footer {
  padding: 12px 14px calc(12px + env(safe-area-inset-bottom));
}

.contract-preview-mask.mobile .contract-preview-footer {
  flex-direction: column;
  align-items: stretch;
}

.contract-preview-mask.mobile .contract-preview-footer p {
  font-size: 11px;
}

.contract-preview-mask.mobile .download-button {
  width: 100%;
}

@keyframes contract-preview-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
